import { createContext, useContext, useState, useEffect, useCallback } from "react";
import api from "../utils/api";
import { paginaDaRota } from "../utils/presencePage";

const OCIOSO_MS = 5 * 60 * 1000;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUser(data);
      return data;
    } catch {
      setUser(false);
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Pulso de presença: conectividade da empresa (só conta com foco) e página
  // aberta por usuário (Auditoria > Acessos). Troca de rota ou de estado pulsa na hora.
  useEffect(() => {
    if (!user?.client_id) return;
    const aba = sessionStorage.getItem("presence_tab") || Math.random().toString(36).slice(2, 10);
    sessionStorage.setItem("presence_tab", aba);
    let ultimaInteracao = Date.now();
    let ultimo = "";
    const estado = () => {
      if (document.visibilityState !== "visible") return "segundo_plano";
      if (!document.hasFocus() || Date.now() - ultimaInteracao > OCIOSO_MS) return "parado";
      return "ativo";
    };
    const pulsar = () => {
      const path = window.location.pathname;
      const corpo = {
        path, ...paginaDaRota(path), estado: estado(), aba,
        foco: document.visibilityState === "visible" && document.hasFocus(),
      };
      ultimo = `${path}|${corpo.pagina}|${corpo.estado}`;
      api.post("/presence/ping", corpo).catch(() => {});
    };
    const verificar = () => {
      const path = window.location.pathname;
      if (`${path}|${paginaDaRota(path).pagina}|${estado()}` !== ultimo) pulsar();
    };
    const interagiu = () => { ultimaInteracao = Date.now(); };
    const sair = () => {
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/presence/leave`, {
        method: "POST", credentials: "include", keepalive: true,
        headers: { "Content-Type": "application/json" }, body: JSON.stringify({ aba }),
      }).catch(() => {});
    };
    pulsar();
    const timer = setInterval(pulsar, 60000);
    const checagem = setInterval(verificar, 2000);
    const eventos = ["mousemove", "keydown", "mousedown", "wheel", "touchstart"];
    eventos.forEach((e) => window.addEventListener(e, interagiu, { passive: true }));
    window.addEventListener("focus", pulsar);
    window.addEventListener("blur", pulsar);
    document.addEventListener("visibilitychange", pulsar);
    window.addEventListener("pagehide", sair);
    return () => {
      clearInterval(timer);
      clearInterval(checagem);
      eventos.forEach((e) => window.removeEventListener(e, interagiu));
      window.removeEventListener("focus", pulsar);
      window.removeEventListener("blur", pulsar);
      document.removeEventListener("visibilitychange", pulsar);
      window.removeEventListener("pagehide", sair);
    };
  }, [user?.client_id]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await api.post("/presence/leave").catch(() => {});
      await api.post("/auth/logout");
    } catch {
      // ignore
    }
    setUser(false);
  };

  const register = async (email, password, name, client_id) => {
    const { data } = await api.post("/auth/register", { email, password, name, client_id });
    setUser(data);
    return data;
  };

  return (
    <AuthContext.Provider value={{ user, loading, login, logout, register, refreshUser: checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
